import React from 'react';
import { getContext } from 'common';
import { useSpring, useTransition, animated } from 'react-spring';
import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';
import Styled, { SPRING_CONFIG } from './styles';
import { ExpansionPanelContext } from './';

const SPRING_ANIMATION = (expanded, resolvedHeight, disableAnimation, props) => ({
  height: expanded ? resolvedHeight : 0,
  config: SPRING_CONFIG,
  onRest: () => {
    if (expanded) {
      props.onAnimationEnd && props.onAnimationEnd();
    }
  },
  immediate: disableAnimation,
});

const TRANSITION_ANIMATION = (height, resolvedHeight, disableAnimation, props) => ({
  from: { height: 0 },
  enter: { height: resolvedHeight },
  leave: { height: 0 },
  update: { height },
  unique: true,
  config: SPRING_CONFIG,
  onRest: (active, _stage, values) => {
    const isFullyExpanded = values.height > 0 && (values.height === resolvedHeight);
    if (active) {
      if (isFullyExpanded) {
        props.onAnimationEnd && props.onAnimationEnd();
      }
    }
  },
  immediate: disableAnimation,
});

export interface ExpansionPanelBodyProps extends React.ComponentPropsWithRef<'div'> {
  /** Place content here. Content in the 'expanded' state would appear here. */
  children: React.ReactNode;
  /** Control the animation. Leverages 'react-spring' useSpring/useTransition. See https://react-spring.io/ */
  animationProps?: any;
  /** Pass props to the wrapper div. */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** Callback function that is triggered when the expansion panel completes its expansion animation. If you are using 'animationProps' this prop will not apply.  */
  onAnimationEnd?: () => void
}

/**
 * Container component for the body portion of ExpansionPanel.
 * Place content here that you intend to show in expanded state.
 * All additional props will be forwarded to the inner wrapper element. To target the outer wrapper, use 'wrapperProps'.
 * */
const ExpansionPanelBody = React.forwardRef<HTMLElement, ExpansionPanelBodyProps>((props: ExpansionPanelBodyProps, ref) =>
  getContext(({ RTL }) => {
    const {
      children, animationProps, wrapperProps, onAnimationEnd, ...otherProps
    } = props;
    const expansionPanelContext = React.useContext(ExpansionPanelContext);
    const { mountContent, expanded, expandedHeight } = expansionPanelContext;
    const [heightRef, { height }] = useMeasure({ polyfill: ResizeObserver });
    const resolvedHeight = expandedHeight || height;
    const resolvedDisableAnimation = expansionPanelContext.disableAnimation;
    const expandedStyle = mountContent ? useSpring(animationProps || SPRING_ANIMATION(expanded, resolvedHeight, resolvedDisableAnimation, props)) : {};
    const expandedTransition = !mountContent ? useTransition(
      expanded,
      null,
      animationProps || TRANSITION_ANIMATION(height, resolvedHeight, resolvedDisableAnimation, props)
    ) : [];
    const content = (
      <Styled.ExpansionPanelBodyInner ref={heightRef} $rtl={RTL} {...otherProps}>
        {children}
      </Styled.ExpansionPanelBodyInner>
    );
    const renderExpandedContentUnmounted = () =>
      expandedTransition.map(({ item, key, props: expandedStylesUnmounted }) =>
        item && (
        <Styled.ExpansionPanelBody
          as={animated.div}
          key={key}
          style={{
            ...expandedStylesUnmounted,
            visibility:
                  expandedStylesUnmounted.height &&
                  expandedStylesUnmounted.height.interpolate(o => (o === 0 ? 'hidden' : 'visible'))
          }}
          $context={expansionPanelContext}
          $rtl={RTL}
          ref={ref}
          tabIndex={!expanded ? '-1' : undefined}
          {...wrapperProps}
        >
          {content}
        </Styled.ExpansionPanelBody>
        ));
    const renderExpandedContentMounted = () => (
      <Styled.ExpansionPanelBody
        as={animated.div}
        style={{
          ...expandedStyle,
          // @ts-expect-error: This might be fixed after updating react-spring
          visibility: expandedStyle.height && expandedStyle.height.interpolate(o => (o === 0 ? 'hidden' : 'visible'))
        }}
        $context={expansionPanelContext}
        ref={ref}
        {...wrapperProps}
      >
        {content}
      </Styled.ExpansionPanelBody>
    );
    return mountContent ? renderExpandedContentMounted() : renderExpandedContentUnmounted();
  }));

ExpansionPanelBody.defaultProps = {
  animationProps: undefined,
  wrapperProps: undefined,
  onAnimationEnd: undefined
};
ExpansionPanelBody.displayName = 'ExpansionPanelBody';

/* Exports */
export { ExpansionPanelBody as default };
